import { Command, CornerDownLeft, Search } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { Avatar } from '@/components/ui/Avatar'
import { api } from '@/lib/api'
import { cn } from '@/lib/cn'
import { Permission } from '@/lib/permissions'
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores/ui'
import type { Member, Page } from '@/types/api'

import { flattenNavigation } from './navigation'

interface Command {
  id: string
  label: string
  group: string
  hint?: string
  /** Present on people, so a face lands next to the name. */
  avatarUrl?: string
  run: () => void
}

/** Pasting an id out of Discord should go straight to that member. */
const SNOWFLAKE = /^\d{15,25}$/

/** Short enough to feel immediate, long enough that typing a name costs one
 *  request rather than one per letter. */
const SEARCH_DEBOUNCE_MS = 220

/** Below this a search matches most of the server, which is not a lookup. */
const MIN_QUERY_LENGTH = 2

export function CommandPalette() {
  const open = useUIStore((state) => state.commandPaletteOpen)
  const setOpen = useUIStore((state) => state.setCommandPaletteOpen)
  const setTheme = useUIStore((state) => state.setTheme)
  const theme = useUIStore((state) => state.theme)
  const navigate = useNavigate()
  const logout = useAuthStore((state) => state.logout)
  const permissions = useAuthStore((state) => state.permissions)
  const isSuperuser = useAuthStore((state) => state.user?.is_superuser ?? false)

  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const [members, setMembers] = useState<Member[]>([])
  const [searching, setSearching] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const canSeeMembers = isSuperuser || permissions.has(Permission.MEMBERS_VIEW)

  // Pages and actions are already in memory; people are not, so they are
  // fetched as the query settles. The cleanup doubles as the guard against an
  // earlier request answering after a later one and overwriting it.
  useEffect(() => {
    const term = query.trim()
    if (!open || !canSeeMembers || term.length < MIN_QUERY_LENGTH || SNOWFLAKE.test(term)) {
      setMembers([])
      setSearching(false)
      return
    }

    let cancelled = false
    setSearching(true)
    const timer = setTimeout(() => {
      void api
        .get<Page<Member>>('/members', {
          query: { search: term, page_size: 6, include_bots: false },
        })
        .then((page) => {
          if (!cancelled) setMembers(page.items)
        })
        .catch(() => {
          // A failed lookup leaves the pages and actions working rather than
          // taking the palette down with it.
          if (!cancelled) setMembers([])
        })
        .finally(() => {
          if (!cancelled) setSearching(false)
        })
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, query, canSeeMembers])

  const commands = useMemo<Command[]>(() => {
    const navigation = flattenNavigation()
      .filter(
        (item) => isSuperuser || item.permissions.some((permission) => permissions.has(permission)),
      )
      .map((item) => ({
        id: `nav-${item.id}`,
        label: item.label,
        group: 'Navigate',
        run: () => navigate(item.to),
      }))

    const actions: Command[] = [
      {
        id: 'theme',
        label: theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
        group: 'Preferences',
        run: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
      },
      {
        id: 'theme-system',
        label: 'Follow the system theme',
        group: 'Preferences',
        run: () => setTheme('system'),
      },
      { id: 'logout', label: 'Sign out', group: 'Account', run: () => void logout() },
    ]

    return [...navigation, ...actions]
  }, [navigate, permissions, isSuperuser, theme, setTheme, logout])

  const filtered = useMemo(() => {
    const term = query.trim()
    if (!term) return commands

    const needle = term.toLowerCase()
    const matches = commands.filter((command) =>
      `${command.label} ${command.group}`.toLowerCase().includes(needle),
    )

    if (!canSeeMembers) return matches

    // An id is unambiguous: offer the one member it names rather than a search
    // that would have to match it as text.
    if (SNOWFLAKE.test(term)) {
      return [
        ...matches,
        {
          id: `member-id-${term}`,
          label: `Open member ${term}`,
          group: 'Members',
          run: () => navigate(`/members/${term}`),
        },
      ]
    }

    return [
      ...matches,
      ...members.map((member) => ({
        id: `member-${member.discord_id}`,
        label: member.display_name,
        hint: `@${member.username}`,
        group: 'Members',
        avatarUrl: member.avatar_url,
        run: () => navigate(`/members/${member.discord_id}`),
      })),
    ]
  }, [commands, query, members, canSeeMembers, navigate])

  // Global shortcut: cmd/ctrl+K anywhere except inside a text field.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen(!useUIStore.getState().commandPaletteOpen)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [setOpen])

  useEffect(() => {
    if (open) {
      setQuery('')
      setCursor(0)
      // Focus after paint, or the browser steals it back during the transition.
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${cursor}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [cursor])

  if (!open) return null

  const runAt = (index: number) => {
    const command = filtered[index]
    if (!command) return
    setOpen(false)
    command.run()
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setCursor((value) => (value + 1) % Math.max(1, filtered.length))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setCursor((value) => (value - 1 + filtered.length) % Math.max(1, filtered.length))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      runAt(cursor)
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  let lastGroup = ''

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center px-4 pt-[12vh]">
      <div
        className="animate-fade-in fixed inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={() => setOpen(false)}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="bg-overlay border-border elevated-lg animate-scale-in relative w-full max-w-xl overflow-hidden rounded-xl border"
      >
        <div className="flex items-center gap-3 border-b border-border px-4">
          <Search
            className="text-subtle-foreground size-4 shrink-0"
            strokeWidth={1.75}
            aria-hidden
          />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setCursor(0)
            }}
            onKeyDown={onKeyDown}
            placeholder={
              canSeeMembers ? 'Search pages, actions and members…' : 'Search pages and actions…'
            }
            className="placeholder:text-subtle-foreground flex-1 bg-transparent py-3.5 text-[14px] outline-none"
          />
          <kbd className="text-subtle-foreground border-border rounded border px-1.5 py-0.5 font-mono text-[10px]">
            ESC
          </kbd>
        </div>

        <div ref={listRef} className="scrollbar-thin max-h-80 overflow-y-auto p-1.5">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center py-10">
              <Command
                className="text-subtle-foreground mb-2 size-5"
                strokeWidth={1.5}
                aria-hidden
              />
              <p className="text-muted-foreground text-[12.5px]">
                {searching ? 'Searching members…' : 'Nothing matches that.'}
              </p>
            </div>
          ) : (
            filtered.map((command, index) => {
              const showHeading = command.group !== lastGroup
              lastGroup = command.group
              return (
                <div key={command.id}>
                  {showHeading && (
                    <p className="text-subtle-foreground px-2.5 pt-2.5 pb-1 text-[10.5px] font-semibold tracking-wider uppercase">
                      {command.group}
                    </p>
                  )}
                  <button
                    data-index={index}
                    onMouseEnter={() => setCursor(index)}
                    onClick={() => runAt(index)}
                    className={cn(
                      'flex w-full items-center justify-between rounded-md px-2.5 py-2 text-left text-[13px]',
                      index === cursor
                        ? 'bg-primary-soft text-primary-text'
                        : 'text-foreground/85 hover:bg-black/[0.04] dark:hover:bg-white/[0.05]',
                    )}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      {command.avatarUrl !== undefined && (
                        <Avatar src={command.avatarUrl} name={command.label} size="xs" />
                      )}
                      <span className="truncate">{command.label}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      {command.hint && (
                        <span className="text-subtle-foreground text-[11.5px]">{command.hint}</span>
                      )}
                      {index === cursor && (
                        <CornerDownLeft className="size-3.5 opacity-60" strokeWidth={2} />
                      )}
                    </span>
                  </button>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
